/** @format */
import { splitAspectRatio } from '@mpb-com/feather/components/atoms/thumbnail/style-helpers';
import { SpringValue, to, useSprings } from '@react-spring/web';
import { FullGestureState, useGesture } from '@use-gesture/react';
import { clamp } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from '@toucan/client/global/nextjs';

import { useMeasure } from '@toucan/client/shared/hooks/use-measure';

import { CarouselProps, CarouselVariant } from './types';

// The percentage width of the image compared to the width of the whole carousel
export const MULTIPLE_IMAGE_WIDTH = 0.6;

// The scale of the non active images
export const MULTIPLE_IMAGE_SCALE = 0.666;

// The opacity of the non active images
export const MULTIPLE_IMAGE_OPACITY = 0.4;

type CarouselHelperArgs = Pick<
    CarouselProps,
    'items' | 'imageAspectRatio' | 'variant' | 'initialPage' | 'maxSlideHeight'
>;

/**
 * Contains the logic for the carousel component
 */
export const useSlide = ({
    items,
    imageAspectRatio = '4/3',
    variant,
    initialPage = 0,
    maxSlideHeight = '100vh',
}: CarouselHelperArgs) => {
    const ulRef = useRef<HTMLUListElement | null>(null);

    /**
     * Needs to be stored in 2 places for the below reasons:
     *
     * - Needs to be stored in state so a re-render can happen for the pagination
     * - Needs to update the state instantly in the handleDrag function so storing
     * in a ref
     */
    const activeIndexRef = useRef<number>(initialPage);
    const [activeIndex, setActiveIndex] = useState<number>(initialPage);

    const bounds = useMeasure(ulRef);

    const showNextButton = Boolean(activeIndex + 1 !== items.length);
    const showPrevButton = Boolean(activeIndex !== 0);

    /**
     * Gets the values used for the styles for a slide
     *
     * @param index The index of the slide
     * @param activeSlideIndex The index of the active slide
     */
    function getSlideStyleValues(index: number, activeSlideIndex: number = 0) {
        if (variant === CarouselVariant.MINIMAL) {
            return {
                opacity: bounds.width === 0 && index !== 0 ? 0 : 1, // if the bounds have not been measured yet only the first slide should be visible as they are all positioned on top of each other
                scale: 1,
                x: index * bounds.width - activeSlideIndex * bounds.width,
            };
        }

        return {
            opacity: activeSlideIndex === index ? 1 : MULTIPLE_IMAGE_OPACITY,
            scale: activeSlideIndex === index ? 1 : MULTIPLE_IMAGE_SCALE,
            x:
                index * bounds.width * MULTIPLE_IMAGE_WIDTH -
                activeSlideIndex * bounds.width * MULTIPLE_IMAGE_WIDTH,
        };
    }

    const [props, set] = useSprings(items.length, (i) => {
        return getSlideStyleValues(i, activeIndexRef.current);
    });

    /**
     * Handles moving the slides when being dragged
     */
    function handleDrag({
        down,
        distance: [distanceX],
        direction: [xDir],
        cancel,
        active,
        movement: [movementX],
    }: Omit<FullGestureState<'drag'>, 'event'> & {
        event: PointerEvent | MouseEvent | TouchEvent | KeyboardEvent;
    }) {
        if (!active) return;

        const snapThreshold =
            variant === CarouselVariant.MINIMAL
                ? bounds.width / 4
                : (bounds.width * MULTIPLE_IMAGE_WIDTH) / 2;

        if (down && distanceX > snapThreshold) {
            const newIndex = clamp(
                activeIndex + (xDir > 0 ? -1 : 1),
                0,
                items.length - 1
            );
            setActiveIndex(newIndex);
            activeIndexRef.current = newIndex;
            cancel && cancel();
        }

        const setFn: any = (i: number) => {
            const { x, scale, opacity } = getSlideStyleValues(
                i,
                activeIndexRef.current
            );

            const animationPercentage =
                distanceX / (bounds.width * MULTIPLE_IMAGE_WIDTH);

            const editCurrentSlide = activeIndexRef.current === i;
            const editNextSlide = i - 1 === activeIndexRef.current && xDir < 0;
            const editPrevSlide = i + 1 === activeIndexRef.current && xDir > 0;

            /**
             * Gets the scale of the slide based on the gesture movements
             */
            function getScale() {
                if (variant === CarouselVariant.MINIMAL) {
                    return 1;
                }

                if (editCurrentSlide) {
                    return scale - MULTIPLE_IMAGE_SCALE * animationPercentage;
                }

                if (editNextSlide || editPrevSlide) {
                    return scale + MULTIPLE_IMAGE_SCALE * animationPercentage;
                }

                return MULTIPLE_IMAGE_SCALE;
            }

            /**
             * Gets the opacity of the slide based on the gesture movements
             */
            function getOpacity() {
                if (variant === CarouselVariant.MINIMAL) {
                    return 1;
                }

                if (editCurrentSlide) {
                    return (
                        opacity - MULTIPLE_IMAGE_OPACITY * animationPercentage
                    );
                }

                if (editNextSlide || editPrevSlide) {
                    return (
                        opacity + MULTIPLE_IMAGE_OPACITY * animationPercentage
                    );
                }

                return MULTIPLE_IMAGE_OPACITY;
            }

            return {
                immediate: false,
                x: x + (down ? distanceX * (xDir > 0 ? 1 : -1) : 0),
                scale: getScale(),
                opacity: getOpacity(),
            };
        };

        set.start(setFn);
    }

    /**
     * Handles resetting the slides to their correct positions
     * when the user stops dragging
     */
    function handleDragEnd() {
        const setFn: any = (i: number) => {
            return {
                immediate: false,
                ...getSlideStyleValues(i, activeIndexRef.current),
            };
        };

        set.start(setFn);
    }

    const bind = useGesture(
        {
            onDrag: handleDrag,
            onDragEnd: handleDragEnd,
        },
        {
            drag: {
                threshold: 10,
                preventScroll: true,
                axis: 'x',
                filterTaps: true,
            },
        }
    );

    /**
     * Animates the correct slide into view and sets internal active index state
     * @param index The new page index
     */
    function handlePageChange(index: number) {
        if (index < 0 || index >= items.length) {
            return;
        }

        setActiveIndex(index);
        activeIndexRef.current = index;

        const setFn: any = (i: number) => {
            return {
                immediate: false,
                ...getSlideStyleValues(i, activeIndexRef.current),
            };
        };

        set.start(setFn);
    }

    type GetSlideStylesArgs = {
        x: SpringValue<number>;
        scale: SpringValue<number>;
        opacity: SpringValue<number>;
        index: number;
    };

    /**
     * Gets the styles for the slide
     */
    function getSlideStyles({ x, scale, opacity, index }: GetSlideStylesArgs) {
        if (variant === CarouselVariant.MINIMAL) {
            return {
                transform: x.to((x: number) => `translate(${x}px)`),
                opacity: opacity.to((opacity: number) => opacity.toString()),
            };
        }

        // Works well with this 🤷‍♀️
        const SCALE_MODIFIER = 1.25;

        return {
            transform: to(
                [x, scale],
                (x, scale) =>
                    `translateX(-50%) translateX(${
                        x * (scale * SCALE_MODIFIER)
                    }px) scale(${scale})`
            ),
            opacity: opacity.to((opacity: number) => opacity.toString()) as any,
        };
    }

    /**
     * Set the height of the carousel based on the aspectRatio when the width
     * of the component changes
     */
    useIsomorphicLayoutEffect(() => {
        if (ulRef.current) {
            // Links to the width of the carousel items
            const imageHeightModifier =
                variant === CarouselVariant.MINIMAL ? 1 : MULTIPLE_IMAGE_WIDTH;

            const { widthRatio, heightRatio } =
                splitAspectRatio(imageAspectRatio);

            const calculatedHeight = `min(${
                bounds.width * (heightRatio / widthRatio) * imageHeightModifier
            }px, ${maxSlideHeight})`;

            ulRef.current.style.height = calculatedHeight;

            /**
             * Updates the slides to where they should be based on the carousel width
             *
             * NOTE: Asserting type to keep ts happy
             */
            const setFn: any = (i: number) => {
                return {
                    immediate: true,
                    ...getSlideStyleValues(i, activeIndexRef.current),
                };
            };

            set.start(setFn);
        }
    }, [bounds.width, imageAspectRatio, variant]);

    useEffect(() => {
        /**
         * Stops firefox from dragging an image when moving the carousel
         */
        function handleImageDragStart(e: Event) {
            if ((e?.target as Element)?.tagName === 'IMG') {
                e.preventDefault();
            }
        }

        if (ulRef.current) {
            ulRef.current.addEventListener('dragstart', handleImageDragStart);
        }

        return () => {
            if (ulRef.current) {
                ulRef.current.removeEventListener(
                    'dragstart',
                    handleImageDragStart
                );
            }
        };
    }, []);

    useEffect(() => {
        handlePageChange(initialPage);
    }, [initialPage]);

    return {
        bind,
        ulRef,
        props,
        activeIndex,
        handlePageChange,
        getSlideStyles,
        showNextButton,
        showPrevButton,
    };
};
